/**
 * 10 thèmes livrés + un thème custom (masque PDF + police + couleur d'accent).
 *
 * Les 6 originaux (corporate, magazine, dashboard, blueprint, minimal, vitrine)
 * restent en place. 4 nouveaux (onyx, ivory, nordic, slate) sont ajoutés à côté.
 * Le "Blueprint tech" (plan d'exécution sur fond bleu nuit + cyan) est ajouté en
 * plus, sous l'ID distinct `blueprint-tech` pour ne pas écraser l'ancien.
 */

export type ThemeId =
  | 'corporate'
  | 'magazine'
  | 'dashboard'
  | 'blueprint'
  | 'minimal'
  | 'vitrine'
  | 'onyx'
  | 'ivory'
  | 'nordic'
  | 'blueprint-tech'
  | 'slate'
  | 'custom';

export type ThemeKind = 'light' | 'dark';

/** Orientation globale de la présentation. Figée par un masque custom. */
export type Orientation = 'portrait' | 'landscape';

/** Configuration d'un thème personnalisé (stockée dans le payload). */
export type CustomTheme = {
  /** Clé R2 du masque PDF (1 ou 2 pages). */
  mask_key?: string;
  /** Nombre de pages du PDF déposé (1 = même masque partout ; 2 = garde + intérieur). */
  mask_pages?: 1 | 2;
  /** Orientation détectée sur le masque (ratio w/h). Verrouille l'orientation de la présentation. */
  mask_orientation?: Orientation;
  /** Famille de police principale (system + 6 Google Fonts autorisées). */
  font?: FontId;
  /** Couleur d'accent (hex). Utilisée pour charts/titres/bordures. */
  accent?: string;
};

export type FontId = 'system' | 'inter' | 'roboto' | 'lato' | 'source-sans' | 'playfair' | 'merriweather';

export const FONT_CHOICES: Array<{ id: FontId; label: string; stack: string }> = [
  { id: 'system', label: 'Système', stack: "-apple-system, 'Segoe UI', Roboto, sans-serif" },
  { id: 'inter', label: 'Inter', stack: "'Inter', sans-serif" },
  { id: 'roboto', label: 'Roboto', stack: "'Roboto', sans-serif" },
  { id: 'lato', label: 'Lato', stack: "'Lato', sans-serif" },
  { id: 'source-sans', label: 'Source Sans', stack: "'Source Sans 3', sans-serif" },
  { id: 'playfair', label: 'Playfair (titres)', stack: "'Playfair Display', serif" },
  { id: 'merriweather', label: 'Merriweather', stack: "'Merriweather', serif" },
];

export const THEMES: Array<{
  id: ThemeId;
  label: string;
  hint: string;
  swatch: string;
  kind: ThemeKind;
}> = [
  // --- 6 thèmes d'origine ---
  { id: 'corporate', label: 'Corporate', hint: 'Classique, aligné, propre',    swatch: '#3D5A45', kind: 'light' },
  { id: 'magazine',  label: 'Magazine',  hint: 'Éditorial, serif, asymétrique', swatch: 'linear-gradient(135deg,#8B5A3C,#C27B5B)', kind: 'light' },
  { id: 'dashboard', label: 'Dashboard', hint: 'Tuilé, dense, chiffres',        swatch: 'linear-gradient(135deg,#587D62,#81B29A)', kind: 'light' },
  { id: 'blueprint', label: 'Blueprint', hint: 'Technique, grille, mono',       swatch: '#0F1724', kind: 'dark' },
  { id: 'minimal',   label: 'Minimal',   hint: 'Impact, un élément / slide',    swatch: '#FFFFFF', kind: 'light' },
  { id: 'vitrine',   label: 'Vitrine',   hint: 'Landing page, immersif',        swatch: 'linear-gradient(135deg,#0A1A12,#E07A5F)', kind: 'dark' },
  // --- 4 nouveaux + variante Blueprint tech ---
  { id: 'onyx',           label: 'Onyx',           hint: 'Noir profond, rouge cramoisi', swatch: 'linear-gradient(135deg,#0A0A0A,#C8102E)', kind: 'dark' },
  { id: 'ivory',          label: 'Ivory',          hint: 'Crème, ocre, typo serif',      swatch: 'linear-gradient(135deg,#F5F1E8,#B07A3A)', kind: 'light' },
  { id: 'nordic',         label: 'Nordic',         hint: 'Blanc cassé, bleu glacier',    swatch: 'linear-gradient(135deg,#FAFAF7,#5B8DB8)', kind: 'light' },
  { id: 'blueprint-tech', label: 'Blueprint Tech', hint: 'Plan d’exécution, cyan + mono', swatch: 'linear-gradient(135deg,#0D1B2A,#00B4D8)', kind: 'dark' },
  { id: 'slate',          label: 'Slate',          hint: 'Ardoise, or pâle, sobre',       swatch: 'linear-gradient(135deg,#1E2530,#D4AF7A)', kind: 'dark' },
  // --- Custom (masque PDF) ---
  { id: 'custom', label: 'Custom', hint: 'Masque PDF + police', swatch: 'repeating-linear-gradient(45deg,#B6B6B6 0 6px,#EAEAEA 6px 12px)', kind: 'light' },
];

export function normalizeThemeId(id: unknown): ThemeId {
  if (typeof id !== 'string') return 'corporate';
  const found = THEMES.find((t) => t.id === id);
  return found ? found.id : 'corporate';
}

export function themeClass(id: ThemeId | string | undefined): string {
  const safe = normalizeThemeId(id);
  return `pres-theme pres-theme-${safe}`;
}

export function themeKind(id: ThemeId): ThemeKind {
  return THEMES.find((t) => t.id === id)?.kind ?? 'light';
}

export function getFont(id: FontId | undefined): string {
  return FONT_CHOICES.find((f) => f.id === id)?.stack ?? FONT_CHOICES[0].stack;
}
